// src/services/payment/planos.js
// 📋 Definição dos planos e limites do sistema
// Versão: 2.0 | Sincronizado com src/config/plans.js (Single Source of Truth)
// Planos: Basic · Professional · Enterprise (3 planos reais)

import { features } from '../../config/features';

/**
 * Limites por plano (usado para verificar permissões)
 * Fonte real: src/config/plans.js
 */
export const PLAN_LIMITS = {
  basic: {
    imoveis: 10,
    clientes: 50,
    usuarios: 1,
    documentos: 100,
    relatorios: 'basic',
    whiteLabel: false,
    api: false,
    suporte: 'email'
  },
  professional: {
    imoveis: 50,
    clientes: 200,
    usuarios: 5,
    documentos: 500,
    relatorios: 'advanced',
    whiteLabel: false,
    api: true,
    suporte: 'prioritario'
  },
  enterprise: {
    imoveis: Infinity,
    clientes: Infinity,
    usuarios: Infinity,
    documentos: Infinity,
    relatorios: 'enterprise',
    whiteLabel: true,
    api: true,
    suporte: 'dedicado'
  },
  // Plano especial para quando pagamentos está desativado (dev/testes)
  unlimited: {
    imoveis: Infinity,
    clientes: Infinity,
    usuarios: Infinity,
    documentos: Infinity,
    relatorios: 'enterprise',
    whiteLabel: true,
    api: true,
    suporte: 'full'
  }
};

/**
 * Características de cada plano (para exibição)
 */
export const PLAN_FEATURES = {
  basic: [
    '✓ Até 10 imóveis',
    '✓ Até 50 clientes',
    '✓ 1 usuário',
    '✓ Calendário de visitas',
    '✓ Relatórios básicos',
    '✓ Suporte por email'
  ],
  professional: [
    '✓ Até 50 imóveis',
    '✓ Até 200 clientes',
    '✓ 5 usuários',
    '✓ Calendário de visitas',
    '✓ Relatórios avançados',
    '✓ API de integração',
    '✓ Suporte prioritário',
    '✓ Exportação Excel/PDF'
  ],
  enterprise: [
    '✓ Imóveis ilimitados',
    '✓ Clientes ilimitados',
    '✓ Usuários ilimitados',
    '✓ Relatórios enterprise + BI',
    '✓ API de integração',
    '✓ Suporte dedicado',
    '✓ White Label completo',
    '✓ Domínio personalizado',
    '✓ Onboarding dedicado'
  ]
};

/**
 * Obtém os limites de um plano
 * @param {string} planId - ID do plano
 * @returns {Object} Limites do plano
 */
export const getPlanLimits = (planId) => {
  return PLAN_LIMITS[planId] || PLAN_LIMITS.basic;
};

/**
 * Obtém as features de um plano para exibição
 * @param {string} planId - ID do plano
 * @returns {Array} Lista de features
 */
export const getPlanFeatures = (planId) => {
  return PLAN_FEATURES[planId] || PLAN_FEATURES.basic;
};

/**
 * Verifica se um plano permite white label
 * @param {string} planId - ID do plano
 * @returns {boolean}
 */
export const hasWhiteLabel = (planId) => {
  return PLAN_LIMITS[planId]?.whiteLabel || false;
};

/**
 * Verifica se um plano tem API access
 * @param {string} planId - ID do plano
 * @returns {boolean}
 */
export const hasApiAccess = (planId) => {
  return PLAN_LIMITS[planId]?.api || false;
};

/**
 * Obtém o limite de um recurso específico
 * @param {string} planId - ID do plano
 * @param {string} resource - Tipo de recurso ('imoveis', 'clientes', etc)
 * @returns {number} Limite
 */
export const getResourceLimit = (planId, resource) => {
  return PLAN_LIMITS[planId]?.[resource] || PLAN_LIMITS.basic[resource] || 0;
};

/**
 * Verifica se um plano permite criar mais de um recurso
 * @param {string} planId - ID do plano
 * @param {string} resource - Tipo de recurso
 * @param {number} currentCount - Quantidade atual
 * @returns {Object} Resultado da verificação
 */
export const canCreateResource = (planId, resource, currentCount) => {
  const limit = getResourceLimit(planId, resource);
  const allowed = currentCount < limit;
  
  return {
    allowed,
    limit,
    current: currentCount,
    remaining: Math.max(0, limit - currentCount),
    message: allowed 
      ? `Você pode criar mais ${resource} (${currentCount}/${limit})`
      : `Limite de ${resource} atingido (${currentCount}/${limit}). Faça upgrade!`
  };
};

/**
 * Obtém o preço de um plano (do features)
 * @param {string} planId - ID do plano
 * @returns {number} Preço em reais
 */
export const getPlanPrice = (planId) => {
  return features.payments?.plans?.[planId]?.price || 0;
};

/**
 * Obtém o nome de um plano
 * @param {string} planId - ID do plano
 * @returns {string} Nome do plano
 */
export const getPlanName = (planId) => {
  const names = {
    basic: 'Básico',
    professional: 'Profissional',
    enterprise: 'Enterprise',
    unlimited: 'Ilimitado'
  };
  return names[planId] || planId;
};

export default {
  PLAN_LIMITS,
  PLAN_FEATURES,
  getPlanLimits,
  getPlanFeatures,
  hasWhiteLabel,
  hasApiAccess,
  getResourceLimit,
  canCreateResource,
  getPlanPrice,
  getPlanName
};